export type EntityStatus = 'ACTIVE' | 'DISABLED' | 'DELETED';

export type MediaPointType =
  'USER' | 'TRUNK' | 'VOICEMAIL' | 'AUTO_ATTENDANT' | 'CALL_ROUTING' |
  'CONFERENCE' | 'PARKING' | 'HUNT_GROUP' | 'RING_GROUP' | 'DID' |
  'PILOT_NUMBER' | 'CALL_QUEUE' | 'FAX' | 'BRIDGE' | 'MEETING' |
  'RECORDING' | 'SITE' | 'TEAMS' | 'UNKNOWN';

export const RECORDABLE_MEDIA_POINT_TYPES: MediaPointType[] = [
  'USER',
  'TRUNK',
  'DID',
  'TEAMS',
  'UNKNOWN',
  'AUTO_ATTENDANT',
  'CALL_QUEUE',
  'CONFERENCE',
  'VOICEMAIL',
];

export interface Group {
  id: string;
  accountId: string;
  name: string;
  description: string | null | undefined;
  status: EntityStatus;
  createdAt: Date;
  updatedAt: Date;
  members?: AppUserMembership[];

  memberCount?: number; // Would like to make this non optional, but short on time and just updating searchGroups for now
  supervisorCount?: number;
}

export interface GroupMembership {
  id?: string;
  groupId: string;
  appUserId: string;
  membershipType: GroupMembershipType;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserInvite {
  inviteDate?: Date;
  inviteAccepted?: boolean;
  inviteAcceptDate?: Date;
}


export interface PortalUserBase {
  id: number;
  name: string;
  email: string;
  roles: string[];
  permissions: string[];
  accessTypes: string[];
  salesforceId: string | null;
  quoteToolDiscount: number;
  readOnly: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface PortalUser extends PortalUserBase {
  type: string;
  isAdmin: boolean;
  isSuperuser: boolean;
  isPartner: boolean;
  isReseller: boolean;
  isCustomerAdmin: boolean;
  isCustomer: boolean;
  isEndUser: boolean;
  isNusoAdmin: boolean;
  isInternalUser: boolean;
  canAccessSubOrgs: boolean;
  canAccessOrg: boolean;
  canAccessOrgOnly: boolean;
  canSeePrices: boolean;
  lastActiveAt?: Date | null;
  userInvite: UserInvite | null;
}

// NOTE: we are deprecating the OWNER for now, but leaving if re-use / change name
export type APP_USER_RECORDING_ROLE = 'USER' | 'SUPERVISOR' | 'ADMINISTRATOR' | 'OWNER';
export enum APP_USER_RECORDING_ROLES_ENUM {
  USER = 'USER',
  SUPERVISOR = 'SUPERVISOR',
  ADMINISTRATOR = 'ADMINISTRATOR',
  OWNER = 'OWNER',
}
export const APP_USER_RECORDING_ROLES = Object.values(APP_USER_RECORDING_ROLES_ENUM)

export interface AppUser {
  id: string;
  accountId: string;
  name: string;
  description?: string;
  language: string;
  status: EntityStatus;
  createdAt: Date
  updatedAt: Date
  permissions: string[];
  portalUser?: PortalUser;
  mediaPoints?: MediaPoint[];
  groups?: Group[];

  recordingRole?: APP_USER_RECORDING_ROLE; // Calculated field

  // Used internally for sub-object query optimization
  _parentObjectType?: string;
}

export type CreateApUserInput = Pick<AppUser, 'name' | 'description' | 'language' | 'permissions'>;
export type UpdateApUserInput = Partial<CreateApUserInput> & Pick<AppUser, 'id'>;

export interface AppUserMembership {
  id: string;
  appUserId: string;
  groupId: string;
  accountId: string;
  name: string;
  description?: string;
  language: string;
  status: EntityStatus;
  membershipType: GroupMembershipType;
  createdAt: Date
  updatedAt: Date
  permissions: string[];
}

export interface AppUserGroupAndRole {
  id: string;
  name: string;
  membershipType: GroupMembershipType;
}

export interface MediaPoint {
  id: string;
  name: string;
  type: MediaPointType;
  serviceUsername: string;
  serviceUserid?: string;
  extensionNumber?: string;
  status: EntityStatus;
  createdAt: Date;
  updatedAt: Date;
  appUserId?: string | null;
  appUserName?: string | null;
  language?: string | null;
  appUser?: AppUser | null;
  serviceId: string;
  serviceName: string;
  serviceType: string;
  serviceTypeName: string;
  serviceRootPath: string;
  accountId: string;
  accountName: string;
  accountNumber: string;
  appUsers?: Pick<AppUser, 'id' | 'name'>[];
}

export interface MediaPointLicense {
  mediaPointId: string;
  license: string;
  quantity: number;
}

export interface MediaPointLicenseDetails {
  mediaPointId: string;
  license: string;
  quantity: number;
  serviceUsername?: string;
}

export type RecordingScope = 'recording.own' | 'recording.group' | 'recording.all';
export const RECORDING_PEMISSION_ACTIONS = ['list', 'view', 'playback', 'download', 'delete', 'confidential'];

export const RECORDING_PERMISSIONS = {
  List_Own: 'recording.own.list',
  View_Own: 'recording.own.view',
  Playback_Own: 'recording.own.playback',
  Download_Own: 'recording.own.download',
  Delete_Own: 'recording.own.delete',

  List_Group: 'recording.group.list',
  View_Group: 'recording.group.view',
  Playback_Group: 'recording.group.playback',
  Download_Group: 'recording.group.download',
  Delete_Group: 'recording.group.delete',
  Confidential_Group: 'recording.group.confidential',

  List_All: 'recording.all.list',
  View_All: 'recording.all.view',
  Playback_All: 'recording.all.playback',
  Download_All: 'recording.all.download',
  Delete_All: 'recording.all.delete',
  Confidential_All: 'recording.all.confidential',
}

export const DEFAULT_RECORDING_USER_PERMISSIONS = [
  RECORDING_PERMISSIONS.List_Own,
  RECORDING_PERMISSIONS.View_Own,
  RECORDING_PERMISSIONS.Playback_Own,
  RECORDING_PERMISSIONS.Download_Own,
  RECORDING_PERMISSIONS.Delete_Own,
];

export const DEFAULT_RECORDING_GROUP_PERMISSIONS = [
  ...DEFAULT_RECORDING_USER_PERMISSIONS,
  RECORDING_PERMISSIONS.List_Group,
  RECORDING_PERMISSIONS.View_Group,
  RECORDING_PERMISSIONS.Playback_Group,
  RECORDING_PERMISSIONS.Download_Group,
  RECORDING_PERMISSIONS.Delete_Group,
  RECORDING_PERMISSIONS.Confidential_Group,
];

export const DEFAULT_RECORDING_ADMIN_PERMISSIONS = [
  ...DEFAULT_RECORDING_GROUP_PERMISSIONS,
  RECORDING_PERMISSIONS.List_All,
];

export type GroupMembershipType = 'MEMBER' | 'SUPERVISOR' | 'MEMBER_AND_SUPERVISOR';

export interface CreateGroupMembershipInput {
  groupId: string;
  appUserMembers: {
    appUserId: string;
    membershipType: GroupMembershipType;
  } [];
}

export interface UpdateGroupMembershipInput {
  groupId: string;
  appUserId: string;
  membershipType: GroupMembershipType;
}

export interface DeleteGroupMembershipInput {
  groupId: string;
  appUserId: string;
}
